import { Component, computed, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { MatIconButton } from '@angular/material/button';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerInputEvent,
  MatDatepickerToggle,
} from '@angular/material/datepicker';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import {
  DateFormats,
  DeviceTypeParameterConfigWithAvailableListAndParameter,
  utcMoment,
} from '@vectronic/util';
import { Moment } from 'moment';

type Config = DeviceTypeParameterConfigWithAvailableListAndParameter;

@Component({
  selector: 'vectronic-parameter-date-time-config',
  templateUrl: './parameter-date-time-config.component.html',
  styleUrls: ['./parameter-date-time-config.component.scss'],
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatFormField,
    MatIcon,
    MatIconButton,
    MatLabel,
    MatSuffix,
    MatTooltip,
    MatOption,
    MatSelect,
    MatDatepickerToggle,
    MatDatepicker,
    MatDatepickerInput,
    MatInput,
  ],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: new DateFormats().returnInternationalDateFormat() },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
  ],
})
export class ParameterDateTimeConfigComponent {
  config = input<Config>();
  value = input<string>();
  disabled = input(false);
  readonly = input<boolean>();

  resetToFactoryTooltip = input<string>();
  hasClose = input(false);
  hideLabel = input<boolean>();
  showInfo = input(true);
  hasDelete = input(true);
  hasUndo = input(false);
  showReset = input<boolean>();

  parameterUndo = output<DeviceTypeParameterConfigWithAvailableListAndParameter>();
  resetToFactory = output<DeviceTypeParameterConfigWithAvailableListAndParameter>();
  configClose = output();
  parameterChange = output<{ config: Config; value: string }>();

  hour = signal<string>(undefined);
  minute = signal<string>(undefined);
  date = signal<Moment>(undefined);
  hasError = computed(() => {
    if (!this.date()?.isValid()) {
      return true;
    }
    const value = this.getSecondsSinceReference(this.date(), this.hour(), this.minute());
    const { validValueRange } = this.config()?.parameter ?? {};
    return Boolean(
      (validValueRange?.max != null && value > validValueRange.max) ||
      (validValueRange?.min != null && value < validValueRange.min),
    );
  });
  calculatedValue = computed(() => {
    if (!this.date()?.isValid()) {
      return undefined;
    }
    const value = this.getSecondsSinceReference(this.date(), this.hour(), this.minute());
    const parsedMax = parseInt(this.config()?.parsedMax);
    if (!isNaN(parsedMax) && value > parsedMax) {
      return parsedMax;
    }
    const parsedMin = parseInt(this.config()?.parsedMin);
    if (!isNaN(parsedMin) && value < parsedMin) {
      return parsedMin;
    }
    return value;
  });
  readonly hours: string[] = Array.from({ length: 25 }, (_, i) => i.toString());
  readonly minutes: string[] = Array.from({ length: 60 }, (_, i) => i.toString());

  parameterTitle = computed(() => {
    const { name, subcategory } = this.config().parameter;
    return subcategory ? `${subcategory} / ${name}` : name;
  });

  constructor() {
    toObservable(this.value)
      .pipe(takeUntilDestroyed())
      .subscribe((val) => {
        const parsed = parseInt(val);
        if (!isNaN(parsed)) {
          this.setDateParts(this.convertSecondsToDateParts(parsed));
        } else {
          this.hour.set(undefined);
          this.minute.set(undefined);
          this.date.set(undefined);
        }
      });
  }

  convertSecondsToDateParts(seconds: number) {
    const momentDate = utcMoment('2000-01-01', false)
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      .add({ second: seconds });

    return {
      date: momentDate.clone().startOf('day'),
      hours: momentDate.hours(),
      minutes: momentDate.minutes(),
    };
  }

  getSecondsSinceReference(date: Moment, hour: string, minute: string): number {
    const reference = utcMoment('2000-01-01', false).set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    });

    const fullDate = date.clone().set({
      hour: parseInt(hour || '0'),
      minute: parseInt(minute || '0'),
      second: 0,
      millisecond: 0,
    });

    return fullDate.diff(reference, 'seconds');
  }

  private setDateParts({ date, hours, minutes }: { date: Moment; hours: number; minutes: number }) {
    this.date.set(date);
    this.hour.set(hours.toString());
    this.minute.set(minutes.toString());
  }

  onClickRemove() {
    this.parameterChange.emit({ config: this.config(), value: undefined });
    this.hour.set(undefined);
    this.minute.set(undefined);
    this.date.set(undefined);
  }

  onClickClose() {
    this.configClose.emit();
  }

  onClickUndo() {
    this.parameterUndo.emit(this.config());
  }

  onResetToFactory() {
    this.resetToFactory.emit(this.config());
  }

  onChangeHour(value: string) {
    this.hour.set(value);
    this.updateValue();
  }

  onChangeMinute(value: string) {
    this.minute.set(value);
    this.updateValue();
  }

  onDateChange($event: MatDatepickerInputEvent<Moment>) {
    this.date.set($event.value?.utc(true).startOf('day') ?? null);
    this.updateValue();
  }

  updateValue() {
    const finalValue = this.calculatedValue();
    this.parameterChange.emit({
      config: this.config(),
      value: finalValue != null && !isNaN(finalValue) ? finalValue.toString() : undefined,
    });
  }
}
